import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { QueryKeys, TranslationNS } from '@gokrew/models/constants';
import type { SaveCandidateOpinionPayload } from '@gokrew/models/recruiting-shares';
import {
  getCandidateReview,
  getCandidateReviewResume,
  getCandidateReviews,
  getCandidateReviewSummary,
  saveCandidateOpinion,
} from '@/api/candidate-reviews';
import { useEmployeePortalAuth } from '@/context/EmployeeAuthContext';

/**
 * The nav entry's numbers. Keyed by the user so a mock-login session never
 * reads the previous person's; refreshed every few minutes for the badge.
 */
export function useCandidateReviewSummary() {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.CandidateReviewSummary, user?.id],
    queryFn: getCandidateReviewSummary,
    enabled: !!user,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

export function useCandidateReviews() {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.CandidateReviews, user?.id],
    queryFn: getCandidateReviews,
    enabled: !!user,
  });
}

/**
 * One share. Opening it records the first view on the server, so the list
 * and the badge refresh once it loads.
 */
export function useCandidateReview(shareId: string | undefined) {
  const { user } = useEmployeePortalAuth();
  return useQuery({
    queryKey: [QueryKeys.CandidateReviews, user?.id, shareId],
    queryFn: () => getCandidateReview(shareId!),
    enabled: !!user && !!shareId,
    retry: false,
  });
}

/**
 * The shared CV's link (10 minutes). Not refetched on focus: a new link
 * would reload the viewer and lose the reader's place.
 */
export function useCandidateReviewResume(shareId: string, enabled: boolean) {
  return useQuery({
    queryKey: [QueryKeys.CandidateReviewResume, shareId],
    queryFn: () => getCandidateReviewResume(shareId),
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 8 * 60_000,
    gcTime: 8 * 60_000,
    retry: false,
  });
}

/** Saves my opinion; the list, the badge and this share refresh. */
export function useSaveCandidateOpinion(shareId: string) {
  const { t } = useTranslation(TranslationNS.Recruiting);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SaveCandidateOpinionPayload) =>
      saveCandidateOpinion(shareId, payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.CandidateReviews],
        }),
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.CandidateReviewSummary],
        }),
      ]);
      toast.success(t('reviews.opinion.saved'));
    },
  });
}
